import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useEffect } from "react";

import Home from "./pages/home/Home";
import NotFound from "./pages/home/NotFound";
import DashboardLayout from "./layouts/DashboardLayout";
import Dashboard from "./pages/programs/mips/Overview";
import MeasureSelection from "./pages/programs/mips/Configuration";
import UploadData from "./pages/programs/mips/UploadData";
import SubmitCMS from "./pages/programs/mips/SubmitCMS";
import SubmitCMSAttestation from "./pages/programs/mips/SubmitCMSAttestation";
import SubmitCMSViewAttestation from "./pages/programs/mips/SubmitCMSViewAttestation";
import Admin from "./pages/Admin";
import Onboarding from "./pages/home/Onboarding";
import QualityWrapper from "./pages/programs/mips/gap-management/QualityWrapper";
import Scorecards from "./pages/programs/mips/Scorecards";
import PaymentAdjustmentReport from "./pages/programs/mips/insights/PaymentAdjustmentReport";
import PathwayReport from "./pages/programs/mips/insights/PathwayReport";
import EligibilityChangesReport from "./pages/programs/mips/insights/EligibilityChangesReport";
import Patients from "./pages/Patients";
import ManageOrganization from './pages/home/ManageOrganization';

import { AuthProvider } from "./contexts/AuthContext";
import userStorage from "@/lib/userStorage";
import { PreferencesProvider } from "./contexts/PreferencesContext";
import { ProgramYearProvider } from "./contexts/ProgramYearContext";
import { ChartAbstractionProvider } from "./contexts/ChartAbstractionContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { ProgramAccessProvider } from "./platform/ProgramAccessContext";
import { AcoProvider } from "./contexts/AcoContext";
import ProgramLauncher from "./pages/home/ProgramLauncher";
import AppPlusModule from "./pages/programs/app-plus/AppPlusModule";

function PracticeGate() {
  // Add mount/unmount tracking
  useEffect(() => {
    console.log('[MOUNT/UNMOUNT] PracticeGate MOUNTED at', new Date().toISOString());
    return () => console.log('[MOUNT/UNMOUNT] PracticeGate UNMOUNTED at', new Date().toISOString());
  }, []);
  
  try {
    const practices = JSON.parse(localStorage.getItem("practices") || "null") || [];
    const single = JSON.parse(localStorage.getItem("practiceInfo") || "null");
    const count = Array.isArray(practices) && practices.length ? practices.length : single ? 1 : 0;
    const current = userStorage.getSessionItem("currentPractice");
    const selectedPracticeId = userStorage.getSessionItem("selectedPracticeId");
    
    console.log('[PracticeGate] routing check', { 
      count, 
      current, 
      selectedPracticeId, 
      practices: practices.length,
      single: !!single,
      path: window.location.pathname 
    });
    
    if (count > 1 && !current) {
      console.log('[PracticeGate] redirecting to /dashboard');
      return <Navigate to="/dashboard" replace />;
    }
  } catch (error) {
    console.log('[PracticeGate] error parsing localStorage', error);
  }
  
  console.log('[PracticeGate] allowing through to', window.location.pathname);
  return <Outlet />;
}


const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <PreferencesProvider>
        <ProgramYearProvider>
          <ProgramAccessProvider>
          <AcoProvider>
          <ChartAbstractionProvider>
            <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Routes>
                
                <Route path="/onboarding" element={<Onboarding />} />
                
                {/* Protected routes */}
                <Route path="/*" element={
                  <ProtectedRoute>
                    <Routes>
                      <Route element={<DashboardLayout />}> 
                        {/* Public-with-sidebar */}
                        <Route path="/" element={<Home />} />

                        {/* Admin is always accessible */}
                        <Route path="/admin" element={<Admin />} />
                        <Route path="/organization/:organizationId" element={<ManageOrganization />} />

                        {/* Program-level routes sit OUTSIDE PracticeGate (P4, EM review B3):
                            program selection precedes practice selection, and ACO work is
                            practice-independent — PracticeGate would bounce a multi-practice
                            user into the MIPS dashboard before they could enter APP Plus. */}
                        <Route path="/launcher" element={<ProgramLauncher />} />
                        <Route path="/programs/app-plus/*" element={<AppPlusModule />} />

                        {/* All other pages require a selected practice if multiple exist */}
                        <Route element={<PracticeGate />}>
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/programs/mips" element={<Dashboard />} />
                          <Route path="/programs/mips/gap-management" element={<Navigate to="/programs/mips/gap-management/quality" replace />} />
                          <Route path="/measure-selection" element={<MeasureSelection />} />
                          <Route path="/upload" element={<UploadData />} />
                          <Route path="/gaps" element={<Navigate to="/programs/mips/gap-management/quality" replace />} />
                          <Route path="/gaps/measure/:id" element={<Navigate to="/programs/mips/gap-management/quality/measure/:id" replace />} />
                          <Route path="/programs/mips/gap-management/quality/*" element={<QualityWrapper />} />
                          <Route path="/scorecards" element={<Scorecards />} />
                          <Route path="/insights/payment-adjustment" element={<PaymentAdjustmentReport />} />
                          <Route path="/insights/pathway-report" element={<PathwayReport />} />
                          <Route path="/insights/eligibility-changes" element={<EligibilityChangesReport />} />
                          <Route path="/patients" element={<Patients />} />
                          <Route path="/submit" element={<SubmitCMS />} />
                          <Route path="/submit/attestation" element={<SubmitCMSAttestation />} />
                          <Route path="/submit/attestation/:id" element={<SubmitCMSViewAttestation />} />
                        </Route>
                      </Route>
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </ProtectedRoute>
                } />
              </Routes>
            </BrowserRouter>
            </TooltipProvider>
          </ChartAbstractionProvider>
          </AcoProvider>
          </ProgramAccessProvider>
        </ProgramYearProvider>
      </PreferencesProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;